/**
 * OtterChat — floating chat widget powered by Gemma 4 agent.
 * Mounts inside Layout, visible on all authenticated pages.
 */

import { useState, useRef, useEffect, FormEvent } from 'react';
import Markdown from 'react-markdown';
import { useChatStream, ChatMessage } from '../hooks/useChatStream';

const GREETING =
  "Hey! I'm Price Otter 🦦 I can help you find deals, track prices, and manage your wishlists. What are you looking for?";

function ConfirmationCard({
  confirmation,
  onConfirm,
}: {
  confirmation: NonNullable<ChatMessage['confirmation']>;
  onConfirm: (confirmationId: string, approved: boolean) => void;
}) {
  const [responded, setResponded] = useState(false);

  const handleClick = (approved: boolean) => {
    setResponded(true);
    onConfirm(confirmation.confirmationId, approved);
  };

  return (
    <div className="bg-muted/50 border border-border rounded-lg p-3 mt-1">
      <div className="text-[13px] text-foreground">{confirmation.message}</div>
      {!responded ? (
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => handleClick(true)}
            className="bg-primary text-primary-foreground px-3 py-1 rounded-md text-[12px] hover:bg-primary/90 transition-colors"
          >
            Go ahead
          </button>
          <button
            onClick={() => handleClick(false)}
            className="bg-muted text-muted-foreground px-3 py-1 rounded-md text-[12px] hover:bg-muted/80 transition-colors"
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="text-[11px] text-muted-foreground mt-1">Responded</div>
      )}
    </div>
  );
}

function ProductCard({ product }: { product: NonNullable<ChatMessage['productCard']> }) {
  return (
    <div className="bg-muted/50 border border-border rounded-lg p-3 mt-1">
      <div className="text-[13px] font-medium text-foreground">{product.name}</div>
      <div className="text-[12px] text-muted-foreground">
        ${product.price.toFixed(2)} at {product.retailer}
        {product.price_change != null && product.price_change < 0 && (
          <span className="text-green-500 ml-1">↓${Math.abs(product.price_change).toFixed(2)}</span>
        )}
      </div>
    </div>
  );
}

export default function OtterChat() {
  const [isOpen, setIsOpen] = useState(false);
  const { messages, isStreaming, toolStatus, sendMessage, sendConfirmation, clearChat } = useChatStream();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming, toolStatus]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleSend = (e: FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || isStreaming) return;

    if (text.toLowerCase() === '/clear') {
      setInput('');
      clearChat();
      return;
    }

    setInput('');
    sendMessage(text);
  };

  // All messages to render: greeting + streamed messages
  const allMessages: Array<{ id: string; role: 'user' | 'assistant'; text: string; msg?: ChatMessage }> = [
    { id: 'greeting', role: 'assistant', text: GREETING },
    ...messages.map(m => ({ id: m.id, role: m.role, text: m.text, msg: m })),
  ];

  return (
    <>
      {/* Chat panel */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: 88,
            right: 24,
            width: 370,
            height: 480,
            zIndex: 50,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 16,
            overflow: 'hidden',
          }}
          className="bg-background border border-border shadow-xl"
        >
          {/* Header */}
          <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border">
            <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-xl">
              🦦
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-foreground">Price Otter</div>
              <div className="text-[11px] text-muted-foreground">Online</div>
            </div>
            <button
              onClick={clearChat}
              title="New chat"
              className="text-muted-foreground hover:text-foreground transition-colors text-sm leading-none"
            >
              ↻
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="text-muted-foreground hover:text-foreground transition-colors text-lg leading-none"
            >
              —
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-2 flex flex-col gap-2.5">
            {allMessages.map((item) =>
              item.role === 'assistant' ? (
                <div key={item.id} className="flex gap-2 items-start">
                  <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs flex-shrink-0 mt-1">
                    🦦
                  </div>
                  <div className="max-w-[80%]">
                    {item.text && (
                      <div className="bg-muted text-foreground px-3.5 py-2.5 rounded-2xl rounded-bl-sm text-[13px] leading-relaxed prose prose-sm prose-neutral dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_strong]:text-black">
                        <Markdown>{item.text}</Markdown>
                      </div>
                    )}
                    {item.msg?.confirmation && (
                      <ConfirmationCard
                        confirmation={item.msg.confirmation}
                        onConfirm={sendConfirmation}
                      />
                    )}
                    {item.msg?.productCard && (
                      <ProductCard product={item.msg.productCard} />
                    )}
                  </div>
                </div>
              ) : (
                <div key={item.id} className="flex justify-end">
                  <div className="bg-primary text-primary-foreground px-3.5 py-2.5 rounded-2xl rounded-br-sm text-[13px] leading-relaxed max-w-[80%]">
                    {item.text}
                  </div>
                </div>
              )
            )}
            {toolStatus && (
              <div className="flex gap-2 items-center">
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs flex-shrink-0">
                  🦦
                </div>
                <div className="text-[11px] text-muted-foreground animate-pulse">
                  {toolStatus}...
                </div>
              </div>
            )}
            {isStreaming && !toolStatus && messages[messages.length - 1]?.role !== 'assistant' && (
              <div className="flex gap-2 items-end">
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs flex-shrink-0">
                  🦦
                </div>
                <div className="bg-muted text-muted-foreground px-3.5 py-2.5 rounded-2xl rounded-bl-sm text-[13px]">
                  <span className="inline-flex gap-1">
                    <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                    <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                    <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSend} className="flex gap-2 px-4 py-3 border-t border-border">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Price Otter anything..."
              className="flex-1 bg-muted border-none rounded-3xl px-4 py-2.5 text-foreground text-[13px] outline-none placeholder:text-muted-foreground"
            />
            <button
              type="submit"
              disabled={!input.trim() || isStreaming}
              className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-base disabled:opacity-40 hover:bg-primary/90 transition-colors"
            >
              ↑
            </button>
          </form>
        </div>
      )}

      {/* FAB */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-background hover:bg-muted text-2xl flex items-center justify-center shadow-lg transition-all z-50 border-2 border-border"
      >
        {isOpen ? '✕' : '🦦'}
      </button>
    </>
  );
}
